import {
  isGmOnlyMember,
  isHrFamilyRole,
  isSameAccount,
} from "./hrStaffHelpers";
import { getAppealSubject } from "./appealSubject";
import { formatAttendanceTime } from "./attendanceTimeFormat";
import { getAppealLevelLabel, isMarketingAppeal } from "./appealCycle";

const REQUEST_STATUS_LABELS = {
  pending: "قيد المراجعة",
  approved: "تمت الموافقة",
  rejected: "مرفوض",
  manager_approved: "موافقة المدير",
  modified: "معدّل",
};

const APPEAL_STATUS_LABELS = {
  manager_pending: "بانتظار المدير",
  regionalManager_pending: "بانتظار مدير المنطقة",
  departmentManager_pending: "بانتظار مدير الإدارة",
  dm_pending: "بانتظار مدير الإدارة",
  hr_pending: "بانتظار HR",
  manager_approved: "موافقة المدير",
  manager_rejected: "رفض المدير",
  regionalManager_approved: "موافقة مدير المنطقة",
  regionalManager_rejected: "رفض مدير المنطقة",
  dm_approved: "موافقة مدير الإدارة",
  dm_rejected: "رفض مدير الإدارة",
  hr_approved: "موافقة HR",
  hr_rejected: "رفض HR",
};

const COMPLAINT_STATUS_LABELS = {
  Pending: "معلق",
  "In Progress": "قيد المعالجة",
  Resolved: "تم الحل",
  Rejected: "مرفوض",
  Closed: "مغلق",
};

function parseDate(value) {
  if (!value) return 0;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

export function canViewEmployeeHistory({
  viewerRole,
  viewer,
  employee,
  getManagedEmployees,
  isHrLoginAccount,
}) {
  if (!employee) return false;

  const isSelf =
    (viewer?.employeeId && employee.id === viewer.employeeId) ||
    (viewer?.id &&
      (employee.userId === viewer.id || employee.id === viewer.id)) ||
    (viewer?.code &&
      employee.code &&
      String(viewer.code).trim() === String(employee.code).trim()) ||
    isSameAccount(employee, viewer);

  if (isSelf) return true;
  if (viewerRole === "gm") return true;

  if (isHrFamilyRole(viewerRole)) {
    if (isGmOnlyMember(employee, isHrLoginAccount)) return false;
    return true;
  }

  const managed = getManagedEmployees(viewer?.employeeId || viewer?.id, viewerRole);
  return managed.some((e) => e.id === employee.id || e.code === employee.code);
}

export function getEmployeeHistory(employee, data) {
  if (!employee) {
    return {
      profile: null,
      requests: [],
      penalties: [],
      appeals: [],
      complaints: [],
      resignations: [],
      leaveCalendar: [],
      handovers: [],
      timeline: [],
      summary: {},
    };
  }

  const allPenalties = [
    ...data.employeePenalties,
    ...data.managerPenalties,
    ...(data.allPenalties || []),
  ];

  const requests = data.pendingRequests.filter(
    (r) => r.employeeCode === employee.code,
  );

  const penalties = allPenalties.filter((p) => p.employeeId === employee.id);

  const appeals = data.appeals.filter((a) => a.employeeId === employee.id);

  const complaints = data.complaints.filter(
    (c) => c.senderId === employee.id,
  );

  const resignations = data.resignations.filter(
    (r) => r.employeeId === employee.id,
  );

  const leaveCalendar = data.leaveCalendarData.filter(
    (l) => l.employee === employee.name,
  );

  const handovers = data.handovers.filter(
    (h) => h.employeeCode === employee.code,
  );

  const timeline = buildTimeline({
    employee,
    requests,
    penalties,
    appeals,
    complaints,
    resignations,
    leaveCalendar,
    handovers,
  });

  return {
    profile: employee,
    requests,
    penalties,
    appeals,
    complaints,
    resignations,
    leaveCalendar,
    handovers,
    timeline,
    summary: {
      requests: requests.length,
      penalties: penalties.length,
      appeals: appeals.length,
      complaints: complaints.length,
      resignations: resignations.length,
      leaveDays: leaveCalendar.length,
      handovers: handovers.length,
      timelineEvents: timeline.length,
    },
  };
}

function buildTimeline(sources) {
  const events = [];

  for (const req of sources.requests) {
    events.push({
      id: `req-${req.id}`,
      module: "request",
      action: req.type,
      timestamp: req.submittedAt,
      summary: `${req.type}${req.reason ? ` — ${req.reason}` : ""}`,
      status: REQUEST_STATUS_LABELS[req.status] || req.status,
      meta: req,
    });
  }

  for (const pen of sources.penalties) {
    events.push({
      id: `pen-${pen.id}`,
      module: "penalty",
      action: pen.type,
      timestamp: pen.date,
      summary: pen.reason,
      status: pen.status === "appealed" ? pen.appealStatus || "معلق" : "ساري",
      meta: pen,
    });
  }

  for (const appeal of sources.appeals) {
    const subject = getAppealSubject(appeal, sources.penalties);
    const subjectBits = [
      subject?.typeLabel,
      subject?.dateLabel ? `يوم ${subject.dateLabel}` : null,
      subject?.whatHappened,
    ].filter(Boolean);
    events.push({
      id: `appeal-${appeal.id}`,
      module: "appeal",
      action: appeal.type,
      timestamp: appeal.submittedAt,
      summary: subjectBits.length
        ? `${subjectBits.join(" — ")}. سبب التظلم: ${appeal.reason}`
        : appeal.reason,
      status:
        isMarketingAppeal(appeal) && appeal.status === "regionalManager_pending"
          ? "بانتظار مدير المدير"
          : isMarketingAppeal(appeal) &&
              appeal.status === "departmentManager_pending"
            ? `بانتظار ${getAppealLevelLabel("departmentManager", appeal)}`
            : APPEAL_STATUS_LABELS[appeal.status] || appeal.status,
      meta: appeal,
    });
  }

  for (const complaint of sources.complaints) {
    events.push({
      id: `complaint-${complaint.id}`,
      module: "complaint",
      action: complaint.title,
      timestamp: complaint.createdAt,
      summary: complaint.description,
      status: COMPLAINT_STATUS_LABELS[complaint.status] || complaint.status,
      meta: complaint,
    });
  }

  for (const resign of sources.resignations) {
    events.push({
      id: `resign-${resign.id}`,
      module: "resignation",
      action: "استقالة",
      timestamp: resign.submittedAt || resign.createdAt,
      summary: resign.reason || "طلب استقالة",
      status: resign.status,
      meta: resign,
    });
  }

  for (const leave of sources.leaveCalendar) {
    events.push({
      id: `leave-${leave.date}-${leave.type}`,
      module: "leave",
      action: leave.type,
      timestamp: leave.date,
      summary: `${leave.type}${leave.days ? ` (${leave.days} يوم)` : ""}${leave.hours ? ` (${leave.hours} ساعة)` : ""}`,
      status: "مسجل",
      meta: leave,
    });
  }

  for (const handover of sources.handovers) {
    events.push({
      id: `handover-${handover.id}`,
      module: "handover",
      action: handover.title,
      timestamp: handover.fromDate,
      summary: handover.description,
      status: handover.status,
      meta: handover,
    });
  }

  return events.sort(
    (a, b) => parseDate(b.timestamp) - parseDate(a.timestamp),
  );
}

export function getModuleLabel(module) {
  const labels = {
    request: "طلب",
    penalty: "جزاء",
    appeal: "تظلم",
    complaint: "شكوى",
    resignation: "استقالة",
    leave: "إجازة",
    permit: "إذن",
    attendance: "حضور",
    employee: "الملف",
    payroll: "راتب",
    handover: "تسليم",
  };
  return labels[module] || module;
}

export function formatHistoryDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("ar-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatHistoryTime(value) {
  return formatAttendanceTime(value);
}

const LEAVE_TYPE_LABELS = {
  ANNUAL: "إجازة اعتيادية",
  SICK: "إجازة مرضية",
  CASUAL: "إجازة عارضة",
  UNPAID: "إجازة على الحساب الخاص",
  COMPENSATORY: "إجازة تعويضية",
  SPECIAL: "إجازة ممنوحة",
};

const OVERTIME_DISPOSITION_LABELS = {
  PENDING: "معلّقة",
  REQUESTED: "طُلب إضافي",
  PAID_OT: "صُرفت ماليًا",
  COMPENSATORY: "تحويل لتعويضي",
};

export function overtimeDispositionLabel(disposition) {
  if (!disposition) return null;
  return OVERTIME_DISPOSITION_LABELS[disposition] || disposition;
}

const ATTENDANCE_STATUS_LABELS = {
  PRESENT: "حاضر",
  LATE: "متأخر",
  ABSENT: "غائب",
  HALF_DAY: "نصف يوم",
  INCOMPLETE: "غير مكتمل",
  REST: "راحة",
};

const REQUEST_STATUS_AR = {
  PENDING: "قيد المراجعة",
  IN_PROGRESS: "قيد الاعتماد",
  APPROVED: "معتمد",
  REJECTED: "مرفوض",
  CANCELLED: "ملغي",
  pending: "قيد المراجعة",
  approved: "معتمد",
  rejected: "مرفوض",
};

export function leaveTypeLabel(type) {
  return LEAVE_TYPE_LABELS[type] || type || "إجازة";
}

export const HR_CONVERT_LEAVE_TYPES = [
  { value: "ANNUAL", label: "اعتيادية" },
  { value: "CASUAL", label: "عارضة" },
  { value: "SICK", label: "مرضية" },
  { value: "UNPAID", label: "على الحساب الخاص" },
];

export const HR_CONVERT_REST_TYPE = { value: "REST", label: "راحة أسبوعية" };

export const HR_CONVERT_DAY_TYPES = [
  HR_CONVERT_REST_TYPE,
  ...HR_CONVERT_LEAVE_TYPES,
];

const ACTIVE_LEAVE_STATUSES = new Set([
  "PENDING",
  "IN_PROGRESS",
  "APPROVED",
  "pending",
  "approved",
  "in_progress",
  "قيد المراجعة",
  "قيد الاعتماد",
  "معتمد",
]);

function dayHasActiveLeave(day) {
  return (day?.leaves || []).some((leave) =>
    ACTIVE_LEAVE_STATUSES.has(leave.status),
  );
}

/** Absence (or past empty) days can be converted to weekly rest as well as leave. */
export function hrConvertDayTargets(day) {
  if (!day) return HR_CONVERT_LEAVE_TYPES;
  if (dayHasActiveLeave(day)) return HR_CONVERT_LEAVE_TYPES;
  const status = String(day.attendance?.status || "").toUpperCase();
  if (status === "REST") return HR_CONVERT_LEAVE_TYPES;
  if (status === "PRESENT" || status === "LATE" || status === "HALF_DAY") {
    return HR_CONVERT_LEAVE_TYPES;
  }
  return HR_CONVERT_DAY_TYPES;
}

function todayIsoUtc() {
  return new Date().toISOString().slice(0, 10);
}

function penaltyLooksLikeEarlyLeave(penalty) {
  const reason = String(penalty?.reason || penalty?.typeLabel || "");
  return /AUTO_EARLY_LEAVE|انصراف مبكر/i.test(reason);
}

export function dayHasEarlyLeavePenalty(day) {
  return (day?.penalties || []).some(penaltyLooksLikeEarlyLeave);
}

function dayAttendanceMinutes(day, field) {
  return Number(day?.attendance?.[field] ?? day?.[field]) || 0;
}

/** HR/GM/Admin may convert uncovered late / early-leave minutes to a permit. */
export function canHrConvertAttendanceToPermit(
  day,
  { role, selfView = false } = {},
) {
  if (selfView || !day) return false;
  if (role !== "admin" && role !== "gm" && !isHrFamilyRole(role)) {
    return false;
  }
  if (dayAttendanceMinutes(day, "lateMinutes") > 0) return true;
  if (dayAttendanceMinutes(day, "earlyMinutes") > 0) return true;
  return dayHasEarlyLeavePenalty(day);
}

/** HR/GM/Admin may convert an absence, weekly rest, or leave day to leave or weekly rest. */
export function canHrConvertLeaveOrAbsenceDay(
  day,
  { role, selfView = false, todayIso } = {},
) {
  if (selfView || !day) return false;
  if (role !== "admin" && role !== "gm" && !isHrFamilyRole(role)) {
    return false;
  }

  if (dayHasActiveLeave(day)) return true;

  const status = String(day.attendance?.status || "").toUpperCase();
  if (status === "ABSENT" || status === "REST") return true;
  if (status === "PRESENT" || status === "LATE" || status === "HALF_DAY") {
    return false;
  }

  const today = todayIso || todayIsoUtc();
  return Boolean(day.date && day.date < today);
}

export function attendanceStatusLabel(status) {
  return ATTENDANCE_STATUS_LABELS[status] || status || "—";
}

export function requestStatusLabel(status) {
  return REQUEST_STATUS_AR[status] || status || "—";
}

function toDateKey(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
}

function eachDateKey(start, end) {
  const from = toDateKey(start);
  const to = toDateKey(end) || from;
  if (!from) return [];
  const keys = [];
  const cursor = new Date(`${from}T00:00:00.000Z`);
  const last = new Date(`${to}T00:00:00.000Z`);
  while (cursor <= last) {
    keys.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return keys;
}

/**
 * Build a day-by-day ledger of attendance, permits, leaves, and penalties.
 */
export function buildEmployeeDailyLedger({
  attendance = [],
  permits = [],
  leaves = [],
  penalties = [],
  from,
  to,
} = {}) {
  const dayMap = new Map();

  function ensureDay(dateKey) {
    if (!dateKey) return null;
    if (!dayMap.has(dateKey)) {
      dayMap.set(dateKey, {
        date: dateKey,
        attendance: null,
        permits: [],
        leaves: [],
        penalties: [],
      });
    }
    return dayMap.get(dateKey);
  }

  const fromKey = toDateKey(from);
  const toKey = toDateKey(to);

  function inRange(dateKey) {
    if (!dateKey) return false;
    if (fromKey && dateKey < fromKey) return false;
    if (toKey && dateKey > toKey) return false;
    return true;
  }

  for (const record of attendance) {
    const key = toDateKey(record.date);
    if (!inRange(key)) continue;
    const day = ensureDay(key);
    day.attendance = record;
  }

  for (const permit of permits) {
    const key = toDateKey(permit.permitDate || permit.date);
    if (!inRange(key)) continue;
    ensureDay(key).permits.push(permit);
  }

  for (const leave of leaves) {
    for (const key of eachDateKey(leave.startDate, leave.endDate)) {
      if (!inRange(key)) continue;
      ensureDay(key).leaves.push(leave);
    }
  }

  for (const penalty of penalties) {
    const key = toDateKey(penalty.effectiveDate || penalty.date || penalty.createdAt);
    if (!inRange(key)) continue;
    ensureDay(key).penalties.push(penalty);
  }

  return Array.from(dayMap.values()).sort((a, b) =>
    a.date < b.date ? 1 : a.date > b.date ? -1 : 0,
  );
}

export function monthBounds(yearMonth) {
  const [yearStr, monthStr] = String(yearMonth || "").split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!year || !month) {
    const now = new Date();
    return monthBounds(
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    );
  }
  const from = new Date(Date.UTC(year, month - 1, 1));
  const to = new Date(Date.UTC(year, month, 0));
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
    year,
    month,
  };
}

export function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}
